import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useEntitlements } from "@/context/EntitlementsContext";

const MAX_POLLS = 15;

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [detail, setDetail] = useState(null);
  const attempts = useRef(0);
  const { refresh } = useEntitlements();

  useEffect(() => {
    if (cancelled) return;
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (refresh) await refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch {
        setState("error");
        return;
      }
      if (attempts.current >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled, refresh]);

  const views = {
    checking: { icon: <Loader2 size={26} className="animate-spin text-slate-400" />, title: "Confirming your payment…",
      body: "This usually takes a couple of seconds. Please do not close this tab." },
    paid: { icon: <CheckCircle2 size={26} className="text-emerald-600" />, title: "You're subscribed",
      body: `Your workspace is now on the ${detail?.plan || "new"} plan with ${detail?.seats || ""} seats. Everything you created during your trial is exactly where you left it.` },
    cancelled: { icon: <XCircle size={26} className="text-slate-400" />, title: "Checkout cancelled",
      body: "Nothing was charged. You can pick a plan again whenever you are ready." },
    failed: { icon: <XCircle size={26} className="text-rose-600" />, title: "That payment did not go through",
      body: "Nothing was charged. Try again, or use a different card." },
    timeout: { icon: <Loader2 size={26} className="text-amber-500" />, title: "Still processing",
      body: "Your payment is taking longer than usual to confirm. It will apply automatically — check the billing page in a minute." },
    error: { icon: <XCircle size={26} className="text-rose-600" />, title: "We could not check that payment",
      body: "If you were charged, your plan will still be applied. Contact us if the billing page does not update." },
  };
  const view = views[state] || views.error;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6" data-testid={`payment-${state}`}>
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="flex justify-center">{view.icon}</div>
        <h1 className="mt-4 font-display text-xl font-semibold text-slate-900">{view.title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600">{view.body}</p>
        <div className="mt-6 flex justify-center gap-2">
          <Link to="/dashboard" data-testid="payment-dashboard-link"
            className="rounded-sm bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-700">
            Go to dashboard
          </Link>
          <Link to="/admin/billing" data-testid="payment-billing-link"
            className="rounded-sm border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-400">
            Billing
          </Link>
        </div>
      </div>
    </div>
  );
}
